import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../../../shared/storage/firebase';
import { ParentSignupFormData, StudentInfo } from '../../../types';
import type { PublicLocation } from '../../partners/types';
import '../styles/ParentSignupForm.css';

interface ParentSignupFormProps {
  onSubmit: (formData: ParentSignupFormData) => Promise<void>;
  loading: boolean;
  error: string | null;
}


const ParentSignupForm: React.FC<ParentSignupFormProps> = ({ onSubmit, loading, error }) => {
  const [formData, setFormData] = useState<ParentSignupFormData>({
    parentFirstName: '',
    parentLastName: '',
    parentEmail: '',
    parentPhone: '',
    students: [{ firstName: '', lastName: '', locationId: '' }],
    password: '',
    tosAccepted: false,
    photoRelease: '',
    inviteToken: ''
  });

  const [locations, setLocations] = useState<PublicLocation[]>([]);
  const [showPassword, setShowPassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Public projection is intentionally safe for unauthenticated signup.
  useEffect(() => {
    const loadLocations = async () => {
      try {
        const locationsRef = collection(db, 'publicLocations');
        const q = query(locationsRef, orderBy('locationName'));
        const snapshot = await getDocs(q);
        setLocations(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as PublicLocation)));
      } catch (error) {
        console.error('Error loading public locations:', error);
        setLocations([]);
      }
    };

    loadLocations();

    // Pre-fill invite token if present in URL
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    if (token) {
      setFormData(prev => ({ ...prev, inviteToken: token }));
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleStudentChange = (index: number, field: keyof StudentInfo, value: string) => {
    setFormData(prev => ({
      ...prev,
      students: prev.students.map((student, i) => 
        i === index ? { ...student, [field]: value } : student
      )
    }));

    // Clear validation errors for this student field
    const errorKey = `student${index}${field}`;
    if (validationErrors[errorKey]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      });
    }
  };

  // Format phone as (XXX) XXX-XXXX while typing
  const formatPhone = (digitsOnly: string) => {
    const d = digitsOnly.replace(/[^0-9]/g, '').slice(0, 10);
    const len = d.length;
    if (len <= 3) return d;
    if (len <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    const formatted = formatPhone(raw);
    setFormData(prev => ({ ...prev, parentPhone: formatted }));

    if (validationErrors.parentPhone) {
      setValidationErrors(prev => {
        const next = { ...prev };
        delete next.parentPhone;
        return next;
      });
    }
  };

  const addStudent = () => {
    setFormData(prev => ({
      ...prev,
      students: [...prev.students, { firstName: '', lastName: '', locationId: '' }]
    }));
  };

  const removeStudent = (index: number) => {
    if (formData.students.length > 1) {
      setFormData(prev => ({
        ...prev,
        students: prev.students.filter((_, i) => i !== index)
      }));

      // Clear validation errors for removed student
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        ['firstName', 'lastName', 'locationId'].forEach(field => {
          delete newErrors[`student${index}${field}`];
        });
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Parent validation
    if (!formData.parentFirstName.trim()) {
      errors.parentFirstName = 'Parent first name is required';
    }

    if (!formData.parentLastName.trim()) {
      errors.parentLastName = 'Parent last name is required';
    }

    if (!formData.parentEmail.trim()) {
      errors.parentEmail = 'Parent email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.parentEmail)) {
      errors.parentEmail = 'Please enter a valid email address';
    }

    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }

    // Optional phone validation (if provided)
    if (formData.parentPhone && !/^\+?[0-9()\-\s]{7,}$/.test(formData.parentPhone)) {
      errors.parentPhone = 'Please enter a valid phone number';
    }

    // Students validation
    formData.students.forEach((student, index) => {
      if (!student.firstName.trim()) {
        errors[`student${index}firstName`] = 'Student first name is required';
      }

      if (!student.lastName.trim()) {
        errors[`student${index}lastName`] = 'Student last name is required';
      }

      if (!student.locationId) {
        errors[`student${index}locationId`] = 'Please select a location';
      }
    });

    // Consent validation
    if (!formData.tosAccepted) {
      errors.tosAccepted = 'You must accept the Terms of Service';
    }

    if (!formData.photoRelease) {
      errors.photoRelease = 'Please select your photo/video release preference';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      await onSubmit(formData);
    } catch (err) {
      console.error('Form submission error:', err);
    }
  };

  return (
    <div className="parent-signup-container">
      <div className="parent-signup-card">
        <div className="parent-signup-header">
          <h1>Create Overture Parent Account</h1>
          <p>Join to see highlights of your child's musical learning journey!</p>
        </div>

        <form onSubmit={handleSubmit} className="parent-signup-form">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="form-section">
            <h3>Parent Information</h3>
            
            <div className="form-group">
              <label htmlFor="parentFirstName">Your First Name *</label>
              <input
                type="text"
                id="parentFirstName"
                name="parentFirstName"
                value={formData.parentFirstName}
                onChange={handleInputChange}
                className={validationErrors.parentFirstName ? 'error' : ''}
                placeholder="Enter your first name"
                disabled={loading}
                required
              />
              {validationErrors.parentFirstName && (
                <span className="field-error">{validationErrors.parentFirstName}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="parentLastName">Your Last Name *</label>
              <input
                type="text"
                id="parentLastName"
                name="parentLastName"
                value={formData.parentLastName}
                onChange={handleInputChange}
                className={validationErrors.parentLastName ? 'error' : ''}
                placeholder="Enter your last name"
                disabled={loading}
                required
              />
              {validationErrors.parentLastName && (
                <span className="field-error">{validationErrors.parentLastName}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="parentEmail">Your Email Address *</label>
              <input
                type="email"
                id="parentEmail"
                name="parentEmail"
                value={formData.parentEmail}
                onChange={handleInputChange}
                className={validationErrors.parentEmail ? 'error' : ''}
                placeholder="Enter your email address"
                disabled={loading}
                required
              />
              {validationErrors.parentEmail && (
                <span className="field-error">{validationErrors.parentEmail}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="parentPhone">Phone Number</label>
              <input
                type="tel"
                id="parentPhone"
                name="parentPhone"
                value={formData.parentPhone}
                onChange={handlePhoneChange}
                className={validationErrors.parentPhone ? 'error' : ''}
                placeholder="(555) 123-4567"
                disabled={loading}
                maxLength={14}
                inputMode="tel"
              />
              {validationErrors.parentPhone && (
                <span className="field-error">{validationErrors.parentPhone}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="password">Create Password *</label>
              <div className="password-input-container">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className={validationErrors.password ? 'error' : ''}
                  placeholder="Create a secure password"
                  disabled={loading}
                  minLength={6}
                  required
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={loading}
                >
                  {showPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
              {validationErrors.password && (
                <span className="field-error">{validationErrors.password}</span>
              )}
            </div>
          </div>

          <div className="form-section">
            <div className="students-header">
              <h3>Student Information</h3>
              <button
                type="button"
                onClick={addStudent}
                className="add-student-button"
                disabled={loading}
              >
                + Add Another Child
              </button>
            </div>
            
            {formData.students.map((student, index) => (
              <div key={index} className="student-card">
                <div className="student-header">
                  <h4>Child #{index + 1}</h4>
                  {formData.students.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeStudent(index)}
                      className="remove-student-button"
                      disabled={loading}
                    >
                      Remove
                    </button>
                  )}
                </div>

                <div className="student-fields">
                  <div className="form-group">
                    <label htmlFor={`student${index}FirstName`}>First Name *</label>
                    <input
                      type="text"
                      id={`student${index}FirstName`}
                      value={student.firstName}
                      onChange={(e) => handleStudentChange(index, 'firstName', e.target.value)}
                      className={validationErrors[`student${index}firstName`] ? 'error' : ''}
                      placeholder="Enter child's first name"
                      disabled={loading}
                      required
                    />
                    {validationErrors[`student${index}firstName`] && (
                      <span className="field-error">{validationErrors[`student${index}firstName`]}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor={`student${index}LastName`}>Last Name *</label>
                    <input
                      type="text"
                      id={`student${index}LastName`}
                      value={student.lastName}
                      onChange={(e) => handleStudentChange(index, 'lastName', e.target.value)}
                      className={validationErrors[`student${index}lastName`] ? 'error' : ''}
                      placeholder="Enter child's last name"
                      disabled={loading}
                      required
                    />
                    {validationErrors[`student${index}lastName`] && (
                      <span className="field-error">{validationErrors[`student${index}lastName`]}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor={`student${index}Location`}>Program location *</label>
                    <select
                      id={`student${index}Location`}
                      value={student.locationId}
                      onChange={(e) => {
                        const selected = locations.find(location => location.id === e.target.value);
                        setFormData(prev => ({
                          ...prev,
                          students: prev.students.map((item, itemIndex) => itemIndex === index ? {
                            ...item,
                            locationId: selected?.id || '',
                            locationSlug: selected?.locationSlug,
                            partnerId: selected?.partnerId,
                          } : item),
                        }));
                      }}
                      className={validationErrors[`student${index}locationId`] ? 'error' : ''}
                      disabled={loading}
                      required
                    >
                      <option value="">Select program location</option>
                      {locations.map(location => (
                        <option key={location.id} value={location.id}>
                          {location.locationName} — {location.partnerName}
                        </option>
                      ))}
                    </select>
                    {validationErrors[`student${index}locationId`] && (
                      <span className="field-error">{validationErrors[`student${index}locationId`]}</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="form-section">
            <h3>Permissions & Consent</h3>
            
            <div className="form-group consent-group">
              <label>
                <input
                  type="checkbox"
                  name="tosAccepted"
                  checked={formData.tosAccepted}
                  onChange={handleInputChange}
                  className={validationErrors.tosAccepted ? 'error' : ''}
                  disabled={loading}
                  required
                />
                <span className="checkmark"></span>
                I accept the <a href="/legal/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a> *
              </label>
              {validationErrors.tosAccepted && (
                <span className="field-error">{validationErrors.tosAccepted}</span>
              )}
            </div>

            <div className="form-group radio-group">
              <fieldset>
                <legend>Photo & Video Release *</legend>
                <p className="radio-description">
                  May we include photos/videos of your child in promotional materials? 
                  <a href="/legal/photo-release" target="_blank" rel="noopener noreferrer">View consent form</a>
                </p>
                
                <label className="radio-label">
                  <input
                    type="radio"
                    name="photoRelease"
                    value="accept"
                    checked={formData.photoRelease === 'accept'}
                    onChange={handleInputChange}
                    disabled={loading}
                    required
                  />
                  <span className="radio-custom"></span>
                  Yes, I give permission
                </label>
                
                <label className="radio-label">
                  <input
                    type="radio"
                    name="photoRelease"
                    value="deny"
                    checked={formData.photoRelease === 'deny'}
                    onChange={handleInputChange}
                    disabled={loading}
                    required
                  />
                  <span className="radio-custom"></span>
                  No, I do not give permission
                </label>

                {validationErrors.photoRelease && (
                  <span className="field-error">{validationErrors.photoRelease}</span>
                )}
              </fieldset>
            </div>

            {formData.inviteToken && (
              <div className="form-group">
                <label htmlFor="inviteToken">Invite Token</label>
                <input
                  type="text"
                  id="inviteToken"
                  name="inviteToken"
                  value={formData.inviteToken}
                  onChange={handleInputChange}
                  placeholder="Optional invite token"
                  disabled={loading}
                  readOnly
                />
                <small>This token will link your account to your child automatically.</small>
              </div>
            )}
          </div>

          <button 
            type="submit" 
            className="parent-signup-submit"
            disabled={loading || !formData.tosAccepted || !formData.photoRelease}
          >
            {loading ? 'Creating Account...' : 'Create Parent Account'}
          </button>
        </form>

        <div className="parent-signup-footer">
          <p>
            Already have an account? <a href="/">Sign in here</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ParentSignupForm;